import {DiseasesState, IDisease, IDiseaseCategory} from '../../models/diseases';
import {cloneDeep} from 'lodash';
import {IRecordEmit} from '../../interfaces/record';
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {Store} from '@ngxs/store';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {Loader} from '../../models/loader/loader.redux';
import {ServiceAction, ServiceState} from '../../models/service';
import {IUser} from '../../models/auth';
import {StorageService} from '../../services/storage/storage.service';
import {ActivatedRoute, Params} from '@angular/router';
import {IMedicalPersonalDisease, IRecordMedicalPersonal, MedicalPersonalState, RecordMedicalPersonalAction} from '../../models/record-medical-personal';
import {NzModalRef} from 'ng-zorro-antd/modal';
import {Logger} from '../../class/helper/logger.class';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {PLATFORM_CONFIG} from '../../config/platform.config';
import {MedicalPersonalDiseaseControlEnum} from '../../enums/medical-personal/medical-personal-disease-control.enum';
import {SkeletonEnum} from '../../enums/skeleton';
import {SkeletonService} from '../../services/skeleton/skeleton.service';

@Component({
  selector: 'app-record-history-personal-medical',
  templateUrl: './record-history-personal-medical.component.html',
  styleUrls: ['./record-history-personal-medical.component.scss'],
  animations: [fadeInAnimation]
})
export class RecordHistoryPersonalMedicalComponent implements OnInit, OnDestroy, OnChanges {
  wasDeployed$: Observable<boolean> = this.store.select<boolean>(MedicalPersonalState.getWasDeployed);
  // variables para la lista de enfermedades
  confirmModal?: NzModalRef;
  serviceId: string;
  expertId: string;
  patientId: string;
  clinicId: string;
  limit = 100;
  userLogged: IUser;
  @Input() recordMedicalPersonal: IRecordMedicalPersonal;
  @Input() isInnerRecord: boolean;
  @Output() recordMedicalPersonalChange: EventEmitter<IRecordEmit> = new EventEmitter<IRecordEmit>();

  wasDeployedSubscription: Subscription;

  diseaseCategories: IDiseaseCategory[] = [];

  loadInfo: boolean;
  skeletonTemplate: any[] = [];
  disableForm = false;

  platformConfig = PLATFORM_CONFIG;
  medicalPersonalDiseaseControlEnum = MedicalPersonalDiseaseControlEnum;
  diseaseControlOptions: MedicalPersonalDiseaseControlEnum[] = [];
  constructor(
    public utilitiesService: UtilitiesService,
    private store: Store,
    private messageAlert: MessageAlertService,
    private storageService: StorageService,
    private activatedRoute: ActivatedRoute,
    private skeletonService: SkeletonService,
  ) {
    this.wasDeployedSubscription = this.wasDeployed$.subscribe(value => {
      if (value && !this.recordMedicalPersonal && !this.isInnerRecord) {
        this.loadInfo = false;
        this.initValuesHttp().then();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.recordMedicalPersonal) {
      this.initValuesHttp().then();
    }
  }

  /**
   * Inicializa las variables iniciales necesarias
   */
  ngOnInit(): void {
    this.skeletonTemplate = this.skeletonService.build(SkeletonEnum.ROWS);

    this.diseaseControlOptions = [
      this.medicalPersonalDiseaseControlEnum.WELL,
      this.medicalPersonalDiseaseControlEnum.FAIR,
      this.medicalPersonalDiseaseControlEnum.BAD,
    ];
  }

  /**
   * Lanza las peticiones iniciales necesarias al servidor.
   */
  async initValuesHttp(): Promise<void> {
    try {
      // await firstValueFrom((this.store.dispatch(new Loader(true, 'Cargando historial médico personal ...'))));
      this.getParams();
      this.userLogged = this.storageService.getLocalItem<{ user: IUser, token: string }>('user')?.user;
      if (this.userLogged && this.userLogged.experts) {
        this.expertId = this.userLogged.experts[0]._id;
      } else if (this.userLogged && this.userLogged.patients) {
        const queryParams: Params = this.activatedRoute.snapshot.queryParams;
        this.expertId = queryParams.expert;
      }
      this.serviceId = await firstValueFrom((this.store.selectOnce(ServiceState.getServiceSelectedId)));
      if (!this.serviceId) {
        await firstValueFrom((this.store.dispatch(new ServiceAction.GetActive(this.patientId, this.expertId))));
        this.serviceId = await firstValueFrom((this.store.selectOnce(ServiceState.getServiceSelectedId)));
      }
      if (!(this.recordMedicalPersonal && this.isInnerRecord)) {
        await firstValueFrom((this.store.dispatch(new RecordMedicalPersonalAction.GetActive(this.serviceId, this.patientId, this.expertId))));
        this.recordMedicalPersonal = cloneDeep(await firstValueFrom((this.store.selectOnce(MedicalPersonalState.getRecordMedicalPersonal))));
      }
      const diseaseCategories = cloneDeep(await firstValueFrom(this.store.selectOnce(DiseasesState.getDiseaseCategories)));

      this.recordMedicalPersonal.startDateProsthesis = this.recordMedicalPersonal.startDateProsthesis ?? undefined;
      diseaseCategories.forEach((category: IDiseaseCategory) => {
        this.diseaseCategories.push({
          ...category,
          // tslint:disable-next-line:variable-name
          diseases: category.diseases ? category.diseases.map((_disease: IDisease) => {
            const existsDisease = this.recordMedicalPersonal.diseases.find(item => item.diseaseId === _disease._id);
            if (existsDisease) existsDisease.control = existsDisease.control ?? MedicalPersonalDiseaseControlEnum.WELL;
            return {
              ..._disease,
              active: !!existsDisease,
              medicine: existsDisease ? existsDisease.medicine : '',
              treatment: existsDisease ? existsDisease.treatment : '',
              control: existsDisease?.control ?? MedicalPersonalDiseaseControlEnum.WELL
            };
            // tslint:disable-next-line:variable-name
          }) : category.diseases ? category.diseases.map((_disease: IDisease) => {
            return {
              ..._disease,
              active: false,
              medicine: '',
              treatment: '',
              control: MedicalPersonalDiseaseControlEnum.WELL
            };
          }) : []
        });
      });
      // Bloquear campos para usuarios pacientes
      this.disableForm = !this.userLogged || !!this.userLogged?.patients;
      setTimeout(() => {
        this.loadInfo = true;
      });
      // se obtiene del record las enfermedades ya mandadas y se hace el check en las de las categorías
      this.recordMedicalPersonalChange.emit({ record: this.recordMedicalPersonal, updated: false });
    } catch (e) {
      Logger.log('Ocurrió un error', e);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  getParams(): void {
    const params: Params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.clinicId = params.clinicId;
    this.patientId = params.patientId;
  }

  checkedDiseaseChange(value: boolean, item: IDisease, categoryName: string): void {
    if (value) {
      // se debe agregar a los datos de la ficha a guardar
      this.pushDisease(item, categoryName);
    } else {
      // se debe remover de los datos de la ficha a guardar
      this.removeDisease(item);
    }
  }

  removeDisease(disease: IDisease): void {
    const existsIndex = this.recordMedicalPersonal.diseases.findIndex(
      // tslint:disable-next-line:variable-name
      (_disease: Partial<IMedicalPersonalDisease>): boolean => _disease.diseaseId === disease._id
    );
    if (existsIndex === -1) return;
    this.recordMedicalPersonal.diseases.splice(existsIndex, 1);
  }

  pushDisease(disease: IDisease, categoryName: string): void {
    const exists: Partial<IMedicalPersonalDisease> = this.recordMedicalPersonal.diseases.find(
      // tslint:disable-next-line:variable-name
      (_disease: Partial<IMedicalPersonalDisease>) => _disease.diseaseId === disease._id
    );
    if (exists) return;
    const item = cloneDeep(disease);
    this.recordMedicalPersonal.diseases.push({
      diseaseCategoryId: item.diseaseCategoryId,
      diseaseCategoryName: categoryName,
      diseaseId: item._id,
      diseaseIdentifier: item.identifier,
      diseaseName: item.name,
      medicine: item.medicine || '',
      treatment: item.treatment || '',
      control: item.control || undefined
    });
  }

  updateTreatment(value: string, item: IDisease): void {
    const existsIndex: number = this.recordMedicalPersonal.diseases.findIndex(
      // tslint:disable-next-line:variable-name
      (_disease: Partial<IMedicalPersonalDisease>) => _disease.diseaseId === item._id
  );
    if (existsIndex === -1) return;
    this.recordMedicalPersonal.diseases[existsIndex].treatment = value;
  }

  updateMedicine(value: string, item: IDisease): void {
    const existsIndex = this.recordMedicalPersonal.diseases.findIndex(
      // tslint:disable-next-line:variable-name
      (_disease: Partial<IMedicalPersonalDisease>) => _disease.diseaseId === item._id
    );
    if (existsIndex === -1) return;
    this.recordMedicalPersonal.diseases[existsIndex].medicine = value;
  }

  updateDiseaseControl(value: MedicalPersonalDiseaseControlEnum, item: IDisease): void {
    const existsIndex = this.recordMedicalPersonal.diseases.findIndex(
      // tslint:disable-next-line:variable-name
      (_disease: Partial<IMedicalPersonalDisease>) => _disease.diseaseId === item._id
    );
    if (existsIndex === -1) return;
    this.recordMedicalPersonal.diseases[existsIndex].control = value;
  }

  onChangeSurgery(value: boolean): void {
    if (value) return;
    this.recordMedicalPersonal.surgeries = undefined;
  }

  onChangeProsthesis(value: boolean): void {
    if (value) return;
    this.recordMedicalPersonal.startDateProsthesis = undefined;
    this.recordMedicalPersonal.prosthesisBodyPart = undefined;
  }

  async onSave(): Promise<void> {
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Guardando enfermedades ...'))));
      const { diseases, hasSurgery, surgeries, hasProsthesis, startDateProsthesis, prosthesisBodyPart } = cloneDeep(this.recordMedicalPersonal);
      await firstValueFrom(this.store.dispatch(new RecordMedicalPersonalAction.PatchActive(
        this.serviceId,
        this.patientId,
        this.expertId,
        {
          diseases,
          hasSurgery,
          surgeries: surgeries || '',
          hasProsthesis,
          startDateProsthesis: startDateProsthesis ?
            typeof startDateProsthesis === 'string' ?
              startDateProsthesis : startDateProsthesis.toISOString() : undefined,
          prosthesisBodyPart: prosthesisBodyPart || ''
        }
      )
      ));
      this.messageAlert.createMessage('success', 'Historial médico personal guardado exitosamente.');
      this.emitChangeUpdate();
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
      this.messageAlert.createMessage('error', 'Ha ocurrido un error al guardar la ficha. Intente más tarde', 5000);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  emitChangeUpdate(): void {
    this.recordMedicalPersonal.updatedAt = new Date();
    this.recordMedicalPersonalChange.emit({ record: this.recordMedicalPersonal, updated: true });
  }

  async ngOnDestroy(): Promise<void> {
    await firstValueFrom((this.store.dispatch(new RecordMedicalPersonalAction.ResetState())));
    this.wasDeployedSubscription.unsubscribe();
  }
}
